import * as React from 'react';
import { styled } from 'styled-components';

// Load all SVGs in src/assets/icons as raw strings at build-time
const svgModules = import.meta.glob('/src/assets/icons/*.svg', {
	query: '?raw',
	import: 'default',
	eager: true,
});

const icons: Record<string, string> = {};
for (const fullPath in svgModules) {
	const parts = fullPath.split('/');
	const fileName = parts[parts.length - 1]; // e.g. trash-01.svg
	const name = fileName.replace(/\.svg$/i, '');
	// @ts-expect-error Vite typing: svgModules values are strings due to ?raw+default
	icons[name] = svgModules[fullPath];
}

const Wrapper = styled.span`
	display: inline-flex;
	line-height: 0;
`;

export interface IconProps {
	name: string; // icon file name without .svg (e.g. "trash-01")
	color?: string; // any CSS color value
	size?: number | string; // width/height, default 20
	className?: string;
	title?: string;
	'aria-label'?: string;
}

// Convert kebab-case and special SVG attribute names to React-friendly prop names
function toReactAttr(name: string): string {
	if (name === 'class') return 'className';
	if (name === 'viewbox') return 'viewBox';
	if (name === 'xlink:href') return 'xlinkHref';
	if (name.includes(':')) return name.replace(':', '');
	return name.replace(/-([a-z])/g, (_m: string, c: string) => c.toUpperCase());
}

function parseStyle(style: string): React.CSSProperties {
	const out: Record<string, string> = {};
	style.split(';').forEach((pair) => {
		const [rawKey, rawVal] = pair.split(':');
		if (!rawKey || !rawVal) return;
		const key = rawKey.trim().replace(/-([a-z])/g, (_m: string, c: string) => c.toUpperCase());
		out[key] = rawVal.trim();
	});
	return out as React.CSSProperties;
}

function nodeToReact(node: Node, isRootSvg = false, size?: number | string): React.ReactNode {
	if (node.nodeType === Node.TEXT_NODE) {
		return (node as Text).nodeValue;
	}
	if (node.nodeType !== Node.ELEMENT_NODE) return null;
	const el = node as Element;
	const tag = el.tagName.toLowerCase();
	const props: Record<string, unknown> = {};

	for (const attr of Array.from(el.attributes)) {
		const key = toReactAttr(attr.name.toLowerCase());
		if (key === 'style') {
			props.style = parseStyle(attr.value);
		} else if (key === 'className') {
			props.className = attr.value;
		} else if (key === 'width' || key === 'height') {
			props[key] = parseInt(attr.value, 10);
		} else {
			props[key] = attr.value;
		}
	}

	const children: React.ReactNode[] = Array.from(el.childNodes).map((child) =>
		nodeToReact(child, false),
	);

	if (isRootSvg) {
		// Ensure accessible, scalable sizing: default width 20, no explicit height
		if (props.height !== undefined) {
			delete props.height;
		}
		// Use provided size or default to 20
		props.width = size ?? 20;
		// Prepend a <style> to drive currentColor on fill, stroke
		const css =
			"*[fill]:not([fill='none']){fill:currentColor} *[stroke]:not([stroke='none']){stroke:currentColor}";
		children.unshift(React.createElement('style', { key: '__icon_style__' }, css));
		props.preserveAspectRatio = 'xMidYMid meet';
	}

	return React.createElement(tag, props, ...children);
}

function buildSvgElement(svgSource: string, size?: number | string) {
	// Parse raw SVG string safely into a DOM and convert to React elements
	const doc = new DOMParser().parseFromString(svgSource, 'image/svg+xml');
	const svgEl = doc.documentElement; // <svg>
	return nodeToReact(svgEl, true, size) as React.ReactElement;
}

export default function Icon({ name, color, size, className, title, ...rest }: IconProps) {
	const svg: string | undefined = icons[name];

	if (!svg) {
		if (import.meta.env.DEV) {
			console.warn(`[Icon] Unknown icon: ${name}. Ensure /src/assets/icons/${name}.svg exists.`);
		}
		return null;
	}

	const labeled = title ?? `${name} icon`;
	const svgElement = buildSvgElement(svg, size);

	return (
		<Wrapper
			role="img"
			className={className}
			style={{ color }}
			{...rest}
			data-icon-name={name}
			data-testid={`icon-${name}`}
			title={labeled}
		>
			{svgElement}
		</Wrapper>
	);
}
